// @flow @jsx h

import {
  Figure,
  Paragraph,
  PullQuote,
  SerumResponsivePicture,
  SmartVideo,
  ViewportIntersections
} from '@nrk/dh-feature-components'
import { bem } from '@nrk/dh-utils'
import { h } from 'preact'
import PureComponent from 'preact-pure-component'
import VisualStory from '../../VisualStory'
import VideoPlayer from '../VideoPlayer'
import style from '../index.css'

import type { Props } from './types'

class Section extends PureComponent<Props, any> {
  elm: HTMLDivElement

  constructor (props: Props) {
    super(props)

    this.state = {
      seen: false
    }
  }

  componentDidMount () {
    this.props.onRegisterSectionElement(this.elm, this.props.index)
  }

  componentDidUpdate (prevProps: Props) {
    const { isIntersecting, onAnalyticsEvent } = this.props
    const { seen } = this.state

    if (!seen && isIntersecting && !prevProps.isIntersecting) {
      setTimeout(() => {
        this.setState({ seen: true })
      }, 0)

      onAnalyticsEvent({ action: `section/SHOW/${this.props.index}` })
    }
  }

  handleIntersectionEnter = (entry: any, index: number) => {
    const { onAnalyticsEvent } = this.props

    onAnalyticsEvent({
      action: `section/SHOW_ITEM/${this.props.index}/${index}`
    })
  }

  render () {
    const {
      index,
      intersections,
      isIntersecting,
      mediaBaseUrl,
      muted,
      onAnalyticsEvent,
      onRegisterTriggerElement,
      onUserToggleMute,
      viewport
    } = this.props
    const { content, id, title } = this.props.value
    const visible = isIntersecting

    return (
      <div
        ref={elm => (this.elm = elm)}
        className={bem(style.section, {
          visible,
          dark: this.props.value.color === 'dark'
        })}
        id={id}
      >
        {title && <h2 className={style.section__title}>{title}</h2>}
        {content && (
          <ViewportIntersections
            onEnter={this.handleIntersectionEnter}
            className={style.section__content}
            threshold={[0]}
          >
            {content.map((item, idx) => {
              switch (item.type) {
                case 'figure':
                  return (
                    <Figure
                      media={
                        <SerumResponsivePicture id={item.value.media.id} />
                      }
                      caption={item.value.caption.map(item => item.value)}
                      key={String(idx)}
                    />
                  )
                case 'pullQuote':
                  return (
                    <PullQuote
                      className={style.article__pullQuote}
                      key={String(idx)}
                      html={item.value.text}
                      cite={item.value.cite}
                    />
                  )
                case 'video':
                  return (
                    <VideoPlayer
                      {...intersections[idx]}
                      key={String(idx)}
                      index={idx}
                      sectionIndex={index}
                      className={style.article__videoPlayer}
                      muted={muted}
                      onAnalyticsEvent={onAnalyticsEvent}
                      onUserToggleMute={onUserToggleMute}
                      onUserTogglePlay={() => {}}
                      onRegisterTriggerElement={onRegisterTriggerElement}
                      video={
                        <SmartVideo
                          playsInline
                          textTracks={item.value.textTracks.map(textTrack => {
                            return {
                              language: textTrack.language || 'Norsk',
                              captions: textTrack.captions
                                ? textTrack.captions.map(caption => {
                                  return {
                                    in: caption.in ? Number(caption.in) : 0,
                                    out: caption.out
                                      ? Number(caption.out)
                                      : 0,
                                    text: String(caption.text).replace(
                                      /<br>/g,
                                      '\n'
                                    )
                                  }
                                })
                                : []
                            }
                          })}
                          resolution720p={{
                            poster: `${mediaBaseUrl}${
                              item.value.resolution720p.poster
                            }`,
                            sources: item.value.resolution720p.sources.map(
                              source => {
                                return {
                                  type: source.type,
                                  src: `${mediaBaseUrl}${source.src}`
                                }
                              }
                            )
                          }}
                          resolution960p={{
                            poster: `${mediaBaseUrl}${
                              item.value.resolution960p.poster
                            }`,
                            sources: item.value.resolution960p.sources.map(
                              source => {
                                return {
                                  type: source.type,
                                  src: `${mediaBaseUrl}${source.src}`
                                }
                              }
                            )
                          }}
                          resolution1080p={{
                            poster: `${mediaBaseUrl}${
                              item.value.resolution1080p.poster
                            }`,
                            sources: item.value.resolution1080p.sources.map(
                              source => {
                                return {
                                  type: source.type,
                                  src: `${mediaBaseUrl}${source.src}`
                                }
                              }
                            )
                          }}
                          viewport={viewport}
                        />
                      }
                    />
                  )
                case 'visualStory':
                  return (
                    <VisualStory
                      {...item.value}
                      {...intersections[idx]}
                      mediaBaseUrl={mediaBaseUrl}
                      muted={muted}
                      onAnalyticsEvent={onAnalyticsEvent}
                      onUserToggleMute={onUserToggleMute}
                      onRegisterTriggerElement={onRegisterTriggerElement}
                      index={idx}
                      sectionIndex={index}
                      viewport={viewport}
                      key={String(idx)}
                    />
                  )
                case 'text':
                  return (
                    <Paragraph
                      className={style.article__paragraph}
                      key={String(idx)}
                      html={item.value}
                    />
                  )
                default:
                  return <div key={String(idx)}>{item.type}</div>
              }
            })}
          </ViewportIntersections>
        )}
      </div>
    )
  }
}

export default Section



// WEBPACK FOOTER //
// ./src/v1/components/Article/Section/index.js